<template>
  <div class="toast-host" aria-live="polite" aria-relevant="additions">
    <TransitionGroup name="toast">
      <div
        v-for="item in store.list"
        :key="item.id"
        class="toast"
        :class="item.tone"
        role="status"
      >
        <div class="toast-body">
          <span class="toast-icon" aria-hidden="true">
            <Icon :name="iconName(item.tone)" :size="16" />
          </span>
          <span class="toast-msg">{{ item.message }}</span>
          <button
            type="button"
            class="toast-close"
            aria-label="关闭"
            @click="store.dismiss(item.id)"
          >
            ×
          </button>
        </div>
        <div class="toast-bar" :style="barStyle(item)" />
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import Icon from '@/components/icons/Icon.vue'
import { useToastStore, type ToastItem, type ToastTone } from '@/stores/toast'

const store = useToastStore()
const now = ref(Date.now())
let raf = 0

function iconName(tone: ToastTone) {
  if (tone === 'success') return 'check'
  if (tone === 'error') return 'alert'
  return 'activity'
}

function barStyle(item: ToastItem) {
  const elapsed = Math.max(0, now.value - item.createdAt)
  const left = Math.max(0, 1 - elapsed / item.durationMs)
  return { transform: `scaleX(${left})` }
}

function tick() {
  now.value = Date.now()
  raf = requestAnimationFrame(tick)
}

onMounted(() => {
  raf = requestAnimationFrame(tick)
})
onUnmounted(() => {
  cancelAnimationFrame(raf)
})
</script>

<style scoped>
.toast-host {
  position: fixed;
  top: calc(12px + env(safe-area-inset-top, 0px));
  right: calc(12px + env(safe-area-inset-right, 0px));
  z-index: var(--z-toast, 1200);
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(360px, calc(100vw - 24px));
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-foreground);
  box-shadow: var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.12));
  overflow: hidden;
}

.toast.success {
  border-color: color-mix(in srgb, var(--color-foreground) 35%, var(--color-border));
}

.toast.error {
  border-color: var(--color-danger, #c44);
  background: color-mix(in srgb, var(--color-danger, #c44) 8%, var(--color-surface));
}

.toast.info {
  border-color: var(--color-border);
}

.toast-body {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
}

.toast-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--color-on-surface-muted);
}

.toast.success .toast-icon {
  color: var(--color-foreground);
}

.toast.error .toast-icon {
  color: var(--color-danger, #c44);
}

.toast-msg {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-sm);
  line-height: 1.4;
  word-break: break-word;
}

.toast-close {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--color-on-surface-subtle);
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
}

.toast-bar {
  height: 2px;
  transform-origin: left center;
  background: var(--color-foreground);
  opacity: 0.35;
  will-change: transform;
}

.toast.error .toast-bar {
  background: var(--color-danger, #c44);
  opacity: 0.7;
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.toast-move {
  transition: transform 0.2s ease;
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active,
  .toast-move {
    transition: none;
  }
}
</style>
